import {
  type ProjectConfig,
  type ProjectId,
  type ProjectReadConfigResult,
} from "@taskin-code/contracts";
import { create } from "zustand";
import { readNativeApi } from "./nativeApi";

type ProjectConfigEntryStatus = "idle" | "loading" | "loaded" | "error";

export interface ProjectConfigEntry {
  status: ProjectConfigEntryStatus;
  exists: boolean;
  valid: boolean;
  config: ProjectConfig | null;
  error: string | null;
  lastLoadedAt: string | null;
}

interface ProjectConfigStoreState {
  byProjectId: Record<ProjectId, ProjectConfigEntry>;
  load: (projectId: ProjectId, cwd: string) => Promise<void>;
  reload: (projectId: ProjectId, cwd: string) => Promise<void>;
}

const INITIAL_ENTRY: ProjectConfigEntry = {
  status: "idle",
  exists: false,
  valid: false,
  config: null,
  error: null,
  lastLoadedAt: null,
};

function toEntry(result: ProjectReadConfigResult): ProjectConfigEntry {
  const lastLoadedAt = new Date().toISOString();
  if (result.exists === false) {
    return {
      status: "loaded",
      exists: false,
      valid: false,
      config: null,
      error: null,
      lastLoadedAt,
    };
  }
  if (result.valid === false) {
    return {
      status: "loaded",
      exists: true,
      valid: false,
      config: null,
      error: result.error,
      lastLoadedAt,
    };
  }
  return {
    status: "loaded",
    exists: true,
    valid: true,
    config: result.config,
    error: null,
    lastLoadedAt,
  };
}

async function fetchProjectConfig(cwd: string): Promise<ProjectConfigEntry> {
  const api = readNativeApi();
  if (!api) {
    return { ...INITIAL_ENTRY, status: "error", error: "Native API unavailable." };
  }
  try {
    const result = await api.projects.readConfig({ cwd });
    return toEntry(result);
  } catch (cause) {
    return {
      ...INITIAL_ENTRY,
      status: "error",
      error: cause instanceof Error ? cause.message : String(cause),
      lastLoadedAt: new Date().toISOString(),
    };
  }
}

export const useProjectConfigStore = create<ProjectConfigStoreState>()((set, get) => ({
  byProjectId: {},
  load: async (projectId, cwd) => {
    const existing = get().byProjectId[projectId];
    if (existing && (existing.status === "loading" || existing.status === "loaded")) {
      return;
    }
    set((state) => ({
      byProjectId: {
        ...state.byProjectId,
        [projectId]: { ...(existing ?? INITIAL_ENTRY), status: "loading" },
      },
    }));
    const entry = await fetchProjectConfig(cwd);
    set((state) => ({
      byProjectId: { ...state.byProjectId, [projectId]: entry },
    }));
  },
  reload: async (projectId, cwd) => {
    set((state) => ({
      byProjectId: {
        ...state.byProjectId,
        [projectId]: { ...(state.byProjectId[projectId] ?? INITIAL_ENTRY), status: "loading" },
      },
    }));
    const entry = await fetchProjectConfig(cwd);
    set((state) => ({
      byProjectId: { ...state.byProjectId, [projectId]: entry },
    }));
  },
}));

export function selectProjectConfigEntry(
  state: ProjectConfigStoreState,
  projectId: ProjectId,
): ProjectConfigEntry | undefined {
  return state.byProjectId[projectId];
}

export function selectProjectHasValidConfig(
  state: ProjectConfigStoreState,
  projectId: ProjectId,
): boolean {
  const entry = state.byProjectId[projectId];
  return entry?.exists === true && entry.valid === true;
}

export function selectProjectPlanModeByDefault(
  state: ProjectConfigStoreState,
  projectId: ProjectId,
): boolean {
  const entry = state.byProjectId[projectId];
  return entry?.valid === true && entry.config?.thread.planModeByDefault === true;
}
